import React, { useState, useEffect, useMemo } from "react";
import DataTable from "react-data-table-component";
import Modal from "react-modal";
import { ArrowLeft, Search, Plus, Download, ChevronDown } from "lucide-react";
import { adminServices } from "../../services/AdminServices";
import { toast } from "react-toastify";
import { logExport } from "../../services/ExportLogService";
if (typeof window !== "undefined" && document.getElementById("root")) {
  Modal.setAppElement("#root");
}
const CategoryModal = ({
  isOpen,
  onClose,
  title,
  initialCategory = "",
  onConfirm,
  loading
}) => {
  const [category, setCategory] = useState(initialCategory);
  useEffect(() => {
    setCategory(initialCategory);
  }, [isOpen, initialCategory]);
  const handleConfirm = async () => {
    const trimmed = (category || "").trim();
    if (!trimmed) {
      toast.warning("Please enter a category name.");
      return;
    }
    try {
      await onConfirm(trimmed);
      onClose();
    } catch {
      // Error handled by parent (toast)
    }
  };
  return <Modal isOpen={isOpen} onRequestClose={onClose} style={{
    overlay: {
      backgroundColor: "rgba(0,0,0,0.5)",
      zIndex: 1000
    },
    content: {
      position: "relative",
      backgroundColor: "#fff",
      borderRadius: "12px",
      maxWidth: "400px",
      width: "100%",
      margin: "auto",
      padding: "24px",
      outline: "none",
      boxShadow: "0 10px 25px rgba(0,0,0,0.2)"
    }
  }} contentLabel={title}>
      <h2 style={{
      margin: "0 0 20px",
      fontSize: "1.125rem",
      fontWeight: 600,
      textAlign: "center"
    }}>{title}</h2>
      <div style={{
      marginBottom: "20px"
    }}>
        <label style={{
        display: "block",
        fontSize: "0.875rem",
        marginBottom: "8px",
        color: "#374151"
      }}>Category</label>
        <input type="text" value={category} onChange={e => setCategory(e.target.value)} placeholder="Enter category name" style={{
        width: "100%",
        padding: "10px 12px",
        border: "1px solid #e5e7eb",
        borderRadius: "6px",
        fontSize: "0.875rem",
        boxSizing: "border-box"
      }} />
      </div>
      <div style={{
      display: "flex",
      justifyContent: "flex-end",
      gap: "12px"
    }}>
        <button type="button" onClick={onClose} style={{
        padding: "8px 16px",
        border: "1px solid #e5e7eb",
        borderRadius: "6px",
        background: "#fff",
        cursor: "pointer",
        fontSize: "0.875rem"
      }}>
          Cancel
        </button>
        <button type="button" onClick={handleConfirm} disabled={loading} style={{
        padding: "8px 16px",
        border: "none",
        borderRadius: "6px",
        background: "#002347",
        color: "#fff",
        cursor: loading ? "not-allowed" : "pointer",
        fontSize: "0.875rem"
      }}>
          {loading ? "Saving..." : "Confirm"}
        </button>
      </div>
    </Modal>;
};
const ClassificationModal = ({
  isOpen,
  onClose,
  categories,
  classifications,
  onConfirm,
  onEdit,
  loading,
  isSubclassification = false,
  editingItem = null
}) => {
  const [productCategoryId, setProductCategoryId] = useState(0);
  const [parentId, setParentId] = useState(0);
  const [name, setName] = useState("");
  const [status, setStatus] = useState("Active");
  useEffect(() => {
    if (isOpen) {
      if (editingItem) {
        setProductCategoryId(editingItem.productCategoryId ?? editingItem.product_category_id ?? 0);
        setParentId(editingItem.parentId ?? editingItem.parent_id ?? 0);
        setName(editingItem.name ?? "");
        setStatus(editingItem.status ?? "Active");
      } else {
        setProductCategoryId(categories[0]?.id ?? 0);
        setParentId(0);
        setName("");
        setStatus("Active");
      }
    }
  }, [isOpen, categories, editingItem]);
  const handleConfirm = async () => {
    const trimmed = (name || "").trim();
    if (!trimmed) {
      toast.warning("Please enter a classification name.");
      return;
    }
    if (!productCategoryId) {
      toast.warning("Please select a category.");
      return;
    }
    if (isSubclassification && (!parentId || parentId === 0)) {
      toast.warning("Please select a parent classification.");
      return;
    }
    try {
      const payload = {
        productCategoryId: Number(productCategoryId),
        parentId: isSubclassification ? Number(parentId) || null : null,
        name: trimmed,
        status: status || "Active"
      };
      if (editingItem?.id) {
        await onEdit?.(editingItem.id, payload);
      } else {
        await onConfirm(payload);
      }
      onClose();
    } catch {
      // Error handled by parent
    }
  };
  const inputStyle = {
    width: "100%",
    padding: "10px 12px",
    border: "1px solid #e5e7eb",
    borderRadius: "6px",
    fontSize: "0.875rem",
    boxSizing: "border-box"
  };
  return <Modal isOpen={isOpen} onRequestClose={onClose} style={{
    overlay: {
      backgroundColor: "rgba(0,0,0,0.5)",
      zIndex: 1000
    },
    content: {
      position: "relative",
      backgroundColor: "#fff",
      borderRadius: "12px",
      maxWidth: "400px",
      width: "100%",
      margin: "auto",
      padding: "24px",
      outline: "none",
      boxShadow: "0 10px 25px rgba(0,0,0,0.2)"
    }
  }} contentLabel={editingItem ? isSubclassification ? "Edit Subclassification" : "Edit Classification" : isSubclassification ? "Add Subclassification" : "Add Classification"}>
      <h2 style={{
      margin: "0 0 20px",
      fontSize: "1.125rem",
      fontWeight: 600,
      textAlign: "center"
    }}>
        {editingItem ? isSubclassification ? "Edit Subclassification" : "Edit Classification" : isSubclassification ? "Add Subclassification" : "Add Classification"}
      </h2>
      <div style={{
      marginBottom: "12px"
    }}>
        <label style={{
        display: "block",
        fontSize: "0.875rem",
        marginBottom: "8px",
        color: "#374151"
      }}>Category</label>
        <select value={productCategoryId} onChange={e => setProductCategoryId(Number(e.target.value))} style={inputStyle}>
          <option value={0}>Select category</option>
          {categories.map(c => <option key={c.id} value={c.id}>{c.category || c.name || "-"}</option>)}
        </select>
      </div>
      {isSubclassification && <div style={{
      marginBottom: "12px"
    }}>
          <label style={{
        display: "block",
        fontSize: "0.875rem",
        marginBottom: "8px",
        color: "#374151"
      }}>Parent Classification</label>
          <select value={parentId} onChange={e => setParentId(Number(e.target.value))} style={inputStyle}>
            <option value={0}>Select parent</option>
            {classifications.filter(c => (c.productCategoryId ?? c.product_category_id) === productCategoryId).map(c => <option key={c.id} value={c.id}>{c.name || "-"}</option>)}
          </select>
        </div>}
      <div style={{
      marginBottom: "12px"
    }}>
        <label style={{
        display: "block",
        fontSize: "0.875rem",
        marginBottom: "8px",
        color: "#374151"
      }}>Name</label>
        <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder={isSubclassification ? "Enter subclassification name" : "Enter classification name"} style={inputStyle} />
      </div>
      <div style={{
      marginBottom: "20px"
    }}>
        <label style={{
        display: "block",
        fontSize: "0.875rem",
        marginBottom: "8px",
        color: "#374151"
      }}>Status</label>
        {/* <input type="text" value={status} onChange={(e) => setStatus(e.target.value)} placeholder="Active" style={inputStyle} /> */}
        <select value={status} onChange={e => setStatus(e.target.value)} style={{
        ...inputStyle,
        marginTop: "8px"
      }}>
          <option value="ACTIVE">Active</option>
          <option value="INACTIVE">Inactive</option>
        </select>
      </div>
      <div style={{
      display: "flex",
      justifyContent: "flex-end",
      gap: "12px"
    }}>
        <button type="button" onClick={onClose} style={{
        padding: "8px 16px",
        border: "1px solid #e5e7eb",
        borderRadius: "6px",
        background: "#fff",
        cursor: "pointer",
        fontSize: "0.875rem"
      }}>Cancel</button>
        <button type="button" onClick={handleConfirm} disabled={loading} style={{
        padding: "8px 16px",
        border: "none",
        borderRadius: "6px",
        background: "#002347",
        color: "#fff",
        cursor: loading ? "not-allowed" : "pointer",
        fontSize: "0.875rem"
      }}>
          {loading ? "Saving..." : editingItem ? "Save" : "Confirm"}
        </button>
      </div>
    </Modal>;
};
const formatDate = val => {
  if (!val) return "-";
  try {
    const d = new Date(val);
    return isNaN(d.getTime()) ? val : d.toLocaleString("en-CA", {
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    }).replace(",", "");
  } catch {
    return val;
  }
};
const CategoryClassificationScreen = ({
  onBack
}) => {
  const [categories, setCategories] = useState([]);
  const [classifications, setClassifications] = useState([]);
  const [subclassifications, setSubclassifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categorySearch, setCategorySearch] = useState("");
  const [classificationSearch, setClassificationSearch] = useState("");
  const [categoryViewMode, setCategoryViewMode] = useState("classification");
  const [isAddCategoryModalOpen, setIsAddCategoryModalOpen] = useState(false);
  const [isEditCategoryModalOpen, setIsEditCategoryModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryModalLoading, setCategoryModalLoading] = useState(false);
  const [isAddClassificationModalOpen, setIsAddClassificationModalOpen] = useState(false);
  const [editingClassification, setEditingClassification] = useState(null);
  const [classificationModalLoading, setClassificationModalLoading] = useState(false);
  const [classificationFilterCategory, setClassificationFilterCategory] = useState("");
  const [showClassificationFilterDropdown, setShowClassificationFilterDropdown] = useState(false);
  const fetchCategories = async () => {
    try {
      const res = await adminServices.getData("ProductCategories");
      const data = res?.data;
      setCategories(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching categories:", err);
      toast.error("Failed to load categories.");
      setCategories([]);
    }
  };
  const fetchClassifications = async () => {
    try {
      const res = await adminServices.storeProcedure2("classifications", {});
      const data = res?.data;
      setClassifications(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching classifications:", err);
      toast.error("Failed to load classifications.");
      setClassifications([]);
    }
  };
  const fetchSubclassifications = async () => {
    try {
      const res = await adminServices.storeProcedure2("subclassifications", {});
      const data = res?.data;
      setSubclassifications(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching subclassifications:", err);
      toast.error("Failed to load subclassifications.");
      setSubclassifications([]);
    }
  };
  const refreshClassificationData = () => {
    fetchClassifications();
    fetchSubclassifications();
  };
  useEffect(() => {
    setLoading(true);
    Promise.all([fetchCategories(), fetchClassifications(), fetchSubclassifications()]).finally(() => setLoading(false));
  }, []);
  useEffect(() => {
    const handleClickOutside = e => {
      if (!e.target.closest(".classification-filter-dropdown")) {
        setShowClassificationFilterDropdown(false);
      }
    };
    if (showClassificationFilterDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showClassificationFilterDropdown]);
  const handleAddCategory = async categoryName => {
    setCategoryModalLoading(true);
    try {
      await adminServices.postData("ProductCategories", {
        category: categoryName,
        description: "",
        productClassifications: []
      });
      toast.success("Category added successfully.");
      fetchCategories();
    } catch (err) {
      console.error("Error adding category:", err);
      toast.error(err.response?.data?.message || "Failed to add category.");
      throw err;
    } finally {
      setCategoryModalLoading(false);
    }
  };
  const handleEditCategory = async categoryName => {
    if (!editingCategory?.id) return;
    setCategoryModalLoading(true);
    try {
      await adminServices.updateData(`ProductCategories/${editingCategory.id}`, {
        ...editingCategory,
        category: categoryName
      });
      toast.success("Category updated successfully.");
      fetchCategories();
      setEditingCategory(null);
    } catch (err) {
      console.error("Error updating category:", err);
      toast.error(err.response?.data?.message || "Failed to update category.");
      throw err;
    } finally {
      setCategoryModalLoading(false);
    }
  };
  const handleEditClassification = async (id, payload) => {
    setClassificationModalLoading(true);
    try {
      await adminServices.updateData(`ProductClassification/${id}`, payload);
      toast.success("Classification updated successfully.");
      refreshClassificationData();
      setEditingClassification(null);
    } catch (err) {
      console.error("Error updating classification:", err);
      toast.error(err.response?.data?.message || "Failed to update classification.");
      throw err;
    } finally {
      setClassificationModalLoading(false);
    }
  };
  const handleAddClassification = async payload => {
    setClassificationModalLoading(true);
    try {
      await adminServices.postData("ProductClassification", payload);
      toast.success("Classification added successfully.");
      refreshClassificationData();
    } catch (err) {
      console.error("Error adding classification:", err);
      toast.error(err.response?.data?.message || "Failed to add classification.");
      throw err;
    } finally {
      setClassificationModalLoading(false);
    }
  };
  const filteredCategories = useMemo(() => {
    const list = !categorySearch.trim() ? categories : categories.filter(c => (c.category || "").toLowerCase().includes(categorySearch.toLowerCase().trim()) || (c.description || "").toLowerCase().includes(categorySearch.toLowerCase().trim()));
    return [...list].sort((a, b) => (a.category || "").localeCompare(b.category || ""));
  }, [categories, categorySearch]);
  const normalizedClassifications = useMemo(() => {
    return (classifications || []).map(c => ({
      ...c,
      productCategoryId: c.productCategoryId ?? c.product_category_id,
      productCategoryName: c.productCategoryName ?? categories.find(cat => cat.id === (c.productCategoryId ?? c.product_category_id))?.category ?? "-",
      name: c.name,
      regDate: c.regDate ?? c.reg_date
    }));
  }, [classifications, categories]);
  const normalizedSubclassifications = useMemo(() => {
    const classMap = Object.fromEntries(classifications.map(c => [c.id, c.name || "-"]));
    const catMap = Object.fromEntries(categories.map(c => [c.id, c.category || c.name || "-"]));
    return (subclassifications || []).map(s => ({
      ...s,
      productCategoryId: s.product_category_id ?? s.productCategoryId,
      productCategoryName: catMap[s.product_category_id ?? s.productCategoryId] ?? "-",
      parentId: s.parent_id ?? s.parentId,
      parentName: classMap[s.parent_id ?? s.parentId] ?? "-",
      name: s.name,
      regDate: s.reg_date ?? s.regDate
    }));
  }, [subclassifications, classifications, categories]);
  const classificationList = categoryViewMode === "classification" ? normalizedClassifications : normalizedSubclassifications;
  const filteredClassifications = useMemo(() => {
    let list = classificationList;
    if (classificationFilterCategory) {
      list = list.filter(c => (c.productCategoryName || "").toLowerCase() === classificationFilterCategory.toLowerCase());
    }
    if (classificationSearch.trim()) {
      const q = classificationSearch.toLowerCase().trim();
      list = list.filter(c => (c.name || "").toLowerCase().includes(q) || (c.productCategoryName || "").toLowerCase().includes(q) || (c.parentName || "").toLowerCase().includes(q));
    }
    return [...list].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [classificationList, classificationSearch, classificationFilterCategory]);
  const exportCategoriesCSV = async () => {
    if (filteredCategories.length === 0) {
      toast.warning("No data to export");
      return;
    }

    // Log the export
    await logExport('Category Classification');
    const headers = ["#", "Category", "Creation Time"];
    const rows = filteredCategories.map((c, i) => [i + 1, c.category || "-", formatDate(c.regDate)]);
    const csv = [headers, ...rows].map(r => r.map(f => `"${String(f).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], {
      type: "text/csv;charset=utf-8;"
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "categories.csv";
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Categories exported");
  };
  const categoryColumns = [{
    name: "#",
    cell: (row, rowIndex) => rowIndex + 1,
    width: "60px"
  }, {
    name: "Category",
    selector: row => row.category || "-",
    sortable: true
  }, {
    name: "Creation Time",
    selector: row => formatDate(row.regDate),
    sortable: true
  }, {
    name: "Actions",
    cell: row => <button type="button" onClick={e => {
      e.stopPropagation();
      setEditingCategory(row);
      setIsEditCategoryModalOpen(true);
    }} style={{
      background: "none",
      border: "none",
      color: "#1E56A0",
      cursor: "pointer",
      textDecoration: "underline",
      fontSize: "0.875rem"
    }}>
          Edit
        </button>,
    width: "100px"
  }];
  const classificationColumns = useMemo(() => [{
    name: "#",
    cell: (row, rowIndex) => rowIndex + 1,
    width: "60px"
  }, {
    name: "Category",
    selector: row => row.productCategoryName || "-",
    sortable: true
  }, ...(categoryViewMode === "subclassification" ? [{
    name: "Classification",
    selector: row => row.parentName || "-",
    sortable: true
  }] : []), {
    name: categoryViewMode === "subclassification" ? "Subclassification" : "Classification",
    selector: row => row.name || "-",
    sortable: true
  }, {
    name: "Creation Time",
    selector: row => formatDate(row.regDate),
    sortable: true
  }, {
    name: "Actions",
    cell: row => <button type="button" onClick={e => {
      e.stopPropagation();
      setEditingClassification(row);
      setIsAddClassificationModalOpen(true);
    }} style={{
      background: "none",
      border: "none",
      color: "#1E56A0",
      cursor: "pointer",
      textDecoration: "underline",
      fontSize: "0.875rem"
    }}>
            Edit
          </button>,
    width: "100px"
  }], [categoryViewMode]);
  const tableStyles = {
    headRow: {
      style: {
        backgroundColor: "#002347",
        color: "#ffffff",
        fontSize: "0.875rem",
        fontWeight: 600
      }
    },
    headCells: {
      style: {
        color: "#ffffff",
        fontSize: "0.875rem",
        fontWeight: 600
      }
    },
    cells: {
      style: {
        fontSize: "0.875rem",
        color: "#111827"
      }
    }
  };
  return <div className="dashboard-content">
      <div className="category-classification-page" style={{
      padding: "24px",
      backgroundColor: "#f9fafb",
      minHeight: "100%"
    }}>
        {/* Header with back */}
        <div style={{
        display: "flex",
        alignItems: "center",
        gap: "16px",
        marginBottom: "24px"
      }}>
          <button type="button" onClick={onBack} style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "40px",
          height: "40px",
          border: "none",
          borderRadius: "8px",
          background: "#fff",
          cursor: "pointer"
        }}>
            <ArrowLeft size={20} />
          </button>
          <h1 style={{
          margin: 0,
          fontSize: "1.5rem",
          fontWeight: 600
        }}>Edit Categories</h1>
        </div>

        {/* Category Section */}
        <div style={{
        background: "#fff",
        borderRadius: "8px",
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        marginBottom: "24px",
        padding: "20px 24px"
      }}>
          <div style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px"
        }}>
            <h2 style={{
            margin: 0,
            fontSize: "1.125rem",
            fontWeight: 600
          }}>Category</h2>
            <button type="button" onClick={() => setIsAddCategoryModalOpen(true)} style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            padding: "8px 16px",
            background: "#D4A017",
            color: "#fff",
            border: "none",
            borderRadius: "6px",
            fontSize: "0.875rem",
            fontWeight: 500,
            cursor: "pointer"
          }}>
              <Plus size={18} />
              Add Category
            </button>
          </div>
          <div style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "16px",
          marginBottom: "20px"
        }}>
            <div style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            padding: "8px 12px",
            background: "#f9fafb",
            border: "1px solid #e5e7eb",
            borderRadius: "6px",
            width: "40%"
          }}>
              <Search size={18} style={{
              color: "#6b7280",
              flexShrink: 0
            }} />
              <input type="text" placeholder="Search" value={categorySearch} onChange={e => setCategorySearch(e.target.value)} style={{
              border: "none",
              background: "transparent",
              outline: "none",
              flex: 1,
              fontSize: "0.875rem",
              minWidth: 0
            }} />
            </div>
            <div style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            flexWrap: "wrap"
          }}>
              <label style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              fontSize: "0.875rem",
              cursor: "pointer"
            }}>
                <input type="radio" name="categoryView" checked={categoryViewMode === "classification"} onChange={() => setCategoryViewMode("classification")} />
                Classification
              </label>
              <label style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              fontSize: "0.875rem",
              cursor: "pointer"
            }}>
                <input type="radio" name="categoryView" checked={categoryViewMode === "subclassification"} onChange={() => setCategoryViewMode("subclassification")} />
                Subclassification
              </label>
              <button type="button" onClick={exportCategoriesCSV} style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "8px 16px",
              background: "#002347",
              color: "#fff",
              border: "none",
              borderRadius: "6px",
              fontSize: "0.875rem",
              cursor: "pointer"
            }}>
                <Download size={18} />
                Export as CSV
              </button>
            </div>
          </div>
          <div style={{
          overflowX: "auto"
        }}>
            <DataTable columns={categoryColumns} data={filteredCategories} customStyles={tableStyles} progressPending={loading} noDataComponent={<div style={{
            padding: "24px",
            textAlign: "center",
            color: "#6b7280"
          }}>No categories found</div>} pagination={false} />
          </div>
        </div>

        {/* Classification / Subclassification Section */}
        <div style={{
        background: "#fff",
        borderRadius: "8px",
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        padding: "20px 24px"
      }}>
          <div style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px"
        }}>
            <h2 style={{
            margin: 0,
            fontSize: "1.125rem",
            fontWeight: 600
          }}>
              {categoryViewMode === "classification" ? "Classification" : "Subclassification"}
            </h2>
            <button type="button" onClick={() => {
            setEditingClassification(null);
            setIsAddClassificationModalOpen(true);
          }} style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            padding: "8px 16px",
            background: "#D4A017",
            color: "#fff",
            border: "none",
            borderRadius: "6px",
            fontSize: "0.875rem",
            fontWeight: 500,
            cursor: "pointer"
          }}>
              <Plus size={18} />
              Add {categoryViewMode === "classification" ? "Classification" : "Subclassification"}
            </button>
          </div>
          <div style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "12px",
          marginBottom: "20px"
        }}>
            <div style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            padding: "8px 12px",
            background: "#f9fafb",
            border: "1px solid #e5e7eb",
            borderRadius: "6px",
            width: "90%"
          }}>
              <Search size={18} style={{
              color: "#6b7280",
              flexShrink: 0
            }} />
              <input type="text" placeholder="Search" value={classificationSearch} onChange={e => setClassificationSearch(e.target.value)} style={{
              border: "none",
              background: "transparent",
              outline: "none",
              flex: 1,
              fontSize: "0.875rem",
              minWidth: 0
            }} />
            </div>
            <div className="classification-filter-dropdown" style={{
            position: "relative"
          }}>
              <button type="button" onClick={() => setShowClassificationFilterDropdown(!showClassificationFilterDropdown)} style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "8px 16px",
              background: "#f3f4f6",
              color: "#111827",
              border: "1px solid #e5e7eb",
              borderRadius: "8px",
              fontSize: "0.875rem",
              fontWeight: 500,
              cursor: "pointer"
            }} title="Filter by category">
                Filters
                <ChevronDown size={16} />
              </button>
              {showClassificationFilterDropdown && <div style={{
              position: "absolute",
              top: "100%",
              right: 0,
              marginTop: "4px",
              backgroundColor: "#fff",
              border: "1px solid #e5e7eb",
              borderRadius: "6px",
              boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
              zIndex: 1000,
              minWidth: "200px",
              maxHeight: "280px",
              overflowY: "auto"
            }}>
                  <button type="button" style={{
                display: "block",
                width: "100%",
                padding: "10px 16px",
                textAlign: "left",
                border: "none",
                backgroundColor: !classificationFilterCategory ? "#f3f4f6" : "transparent",
                cursor: "pointer",
                fontSize: "0.875rem"
              }} onClick={() => {
                setClassificationFilterCategory("");
                setShowClassificationFilterDropdown(false);
              }}>
                    All Categories
                  </button>
                  {categories.map(cat => {
                const catName = cat.category || cat.name || "-";
                const isSelected = classificationFilterCategory === catName;
                return <button key={cat.id} type="button" style={{
                  display: "block",
                  width: "100%",
                  padding: "10px 16px",
                  textAlign: "left",
                  border: "none",
                  borderTop: "1px solid #e5e7eb",
                  backgroundColor: isSelected ? "#f3f4f6" : "transparent",
                  cursor: "pointer",
                  fontSize: "0.875rem"
                }} onClick={() => {
                  setClassificationFilterCategory(catName);
                  setShowClassificationFilterDropdown(false);
                }}>
                        {catName}
                      </button>;
              })}
                </div>}
            </div>
          </div>
          <div style={{
          overflowX: "auto"
        }}>
            <DataTable columns={classificationColumns} data={filteredClassifications} customStyles={tableStyles} progressPending={loading} noDataComponent={<div style={{
            padding: "24px",
            textAlign: "center",
            color: "#6b7280"
          }}>
                  {categoryViewMode === "subclassification" ? "No subclassifications found" : "No classifications found"}
                </div>} pagination={false} />
          </div>
        </div>

        {/* Add Category Modal */}
        <CategoryModal isOpen={isAddCategoryModalOpen} onClose={() => setIsAddCategoryModalOpen(false)} title="Add Category" initialCategory="" onConfirm={handleAddCategory} loading={categoryModalLoading} />

        {/* Edit Category Modal */}
        <CategoryModal isOpen={isEditCategoryModalOpen} onClose={() => {
        setIsEditCategoryModalOpen(false);
        setEditingCategory(null);
      }} title="Edit Category" initialCategory={editingCategory?.category || ""} onConfirm={handleEditCategory} loading={categoryModalLoading} />

        {/* Add / Edit Classification Modal */}
        <ClassificationModal isOpen={isAddClassificationModalOpen} onClose={() => {
        setIsAddClassificationModalOpen(false);
        setEditingClassification(null);
      }} categories={categories} classifications={normalizedClassifications} onConfirm={handleAddClassification} onEdit={handleEditClassification} loading={classificationModalLoading} isSubclassification={categoryViewMode === "subclassification"} editingItem={editingClassification} />
      </div>
    </div>;
};
export default CategoryClassificationScreen;